<script setup>
import { ref, computed, onMounted, onUnmounted, getCurrentInstance } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import { useWindowSize } from '@vueuse/core'
import useApi from "@/services/useApi"
import { showToast } from "@/helpers/showToast"
import messengers from "@/data/messengers.js"

// Components
import HeaderMain from "@/components/Recurring/HeaderMain.vue"
import TableNumbersTariffs from "@/components/Tariffs/TableNumbersTariffs.vue"
import MobileTableRecurring from "@/components/Recurring/MobileTableRecurring.vue"
import HeaderTableBlock from "@/components/ui/table/HeaderTableBlock.vue"
import NumberTagsList from "@/components/Tariffs/NumberTagsList.vue"
import BasePagination from "@/components/ui/BasePagination.vue"
import InfoDataTextRecurring from "@/components/Recurring/InfoDataTextRecurring.vue"
import ButtonLoading from "@/components/Recurring/ButtonLoading.vue"
import {useI18n} from "vue-i18n";

const store = useStore()
const router = useRouter()
const route = useRoute()
const { t: $t, } = useI18n();
const {proxy} = getCurrentInstance();
const { width } = useWindowSize()

// State
const windowSize = ref(0)
const searchQuery = ref("")
const confirmLoading = ref(false)

// Computed
const numbersViber = computed(() => store.state.numbers.numbersDataViber[0])
const numbersTelegram = computed(() => store.state.numbers.numbersDataTelegram[0])
const numbersWhatsapp = computed(() => store.state.numbers.numbersDataWhatsapp[0])

const paginationAllMessengers = computed(() => store.state.numbers.numbersDataAttributes?.numberListPagination)

const numbersViberLoading = computed(() => store.state.numbers.numbersDataAttributes.loading.viber[0])
const numbersTelegramLoading = computed(() => store.state.numbers.numbersDataAttributes.loading.telegram[0])
const numberWhatsappLoading = computed(() => store.state.numbers.numbersDataAttributes.loading.whatsapp[0])

const numbersViberSearch = computed(() => store.state.numbers.numbersDataAttributes.searchInput.viber[0])
const numbersTelegramSearch = computed(() => store.state.numbers.numbersDataAttributes.searchInput.telegram[0])
const numbersWhatsappSearch = computed(() => store.state.numbers.numbersDataAttributes.searchInput.whatsapp[0])

const preselectedNumbers = computed(() => store.state.numbers.preselectedNumbers)

const tableState = computed(() => ({
    viber: {
        data: numbersViber.value,
        isLoading: numbersViberLoading.value,
        search: numbersViberSearch.value,
        statusState: numbersViberLoading.value ? 'loading' : !numbersViber.value?.length ? 'noNumbers' : null
    },
    telegram: {
        data: numbersTelegram.value,
        isLoading: numbersTelegramLoading.value,
        search: numbersTelegramSearch.value,
        statusState: numbersTelegramLoading.value ? 'loading' : !numbersTelegram.value?.length ? 'noNumbers' : null
    },
    whatsapp: {
        data: numbersWhatsapp.value,
        isLoading: numberWhatsappLoading.value,
        search: numbersWhatsappSearch.value,
        statusState: numberWhatsappLoading.value ? 'loading' : !numbersWhatsapp.value?.length ? 'noNumbers' : null
    }
}))

const getTotalSelectedNumbersCount = computed(() =>
    Object.values(preselectedNumbers.value).reduce((sum, arr) => sum + arr.length, 0)
)

// Methods
const changePage = (numberPage, messenger) => {
    const visibility = true
    const number = store.state.numbers.numbersDataAttributes.searchInput[messenger][0] || null

    store.dispatch("numbers/loadNumbersByMessenger", {
        messenger,
        number,
        page: numberPage,
        visibility,
        isCollectData: false,
        prepayment: false,
        tariff_end_date: true
    })
}

const getNumberDataBySearchValue = (input, messenger) => {
    const visibility = true

    store.dispatch("numbers/setSearchInput", { input, messenger, visibility })

    store.dispatch("numbers/loadNumbersByMessenger", {
        number: input,
        messenger,
        visibility,
        prepayment: false,
        tariff_end_date: true
    })
}

const loadMore = (messenger) => {
    const visibility = true
    const number = store.state.numbers.numbersDataAttributes.searchInput[messenger][0] || null

    const pageData = paginationAllMessengers.value[messenger][0]
    const isLastPage = pageData.current_page === pageData.last_page
    const page = !isLastPage && pageData.current_page + 1

    if (!isLastPage) {
        store.dispatch("numbers/loadNumbersByMessenger", {
            messenger,
            number,
            page,
            visibility,
            prepayment: false,
            tariff_end_date: true
        })
    }
}

const loadNumbersData = async () => {
    const promises = [
        await store.dispatch("numbers/loadNumbersByMessenger", {
            messenger: "viber",
            visibility: true,
            prepayment: false,
            tariff_end_date: true
        }),
        await store.dispatch("numbers/loadNumbersByMessenger", {
            messenger: "telegram",
            visibility: true,
            prepayment: false,
            tariff_end_date: true
        }),
        await store.dispatch("numbers/loadNumbersByMessenger", {
            messenger: "whatsapp",
            visibility: true,
            prepayment: false,
            tariff_end_date: true
        })
    ]

    await Promise.allSettled(promises)
}

const unselectAllByMsg = (messenger) => {
    updatePreselectedNumbers([], messenger)
}

const unselectSimpleByMsg = (messenger, number) => {
    const list = preselectedNumbers.value[messenger]
    const filtered = list.filter(v => v.number !== number)
    updatePreselectedNumbers(filtered, messenger)
}

const updatePreselectedNumbers = (list, messenger) => {
    store.commit("numbers/SET_PRESELECTED_NUMBERS_BY_MSG", {
        data: list,
        msg: messenger
    })
}

const onResize = () => {
    windowSize.value = window.innerWidth
}

const isSelectedNumber = (number, messenger) => {
    if (!number || !messenger) return
    return preselectedNumbers.value[messenger]?.some(v => v.number === number)
}

const toggleSelection = (item, messenger) => {
    const list = preselectedNumbers.value[messenger]
    const isExist = isSelectedNumber(item.number, messenger)
    const updatedArray = !isExist
        ? [...list, item]
        : list.filter(v => v.number !== item.number)

    store.commit("numbers/SET_PRESELECTED_NUMBERS_BY_MSG", { data: updatedArray, msg: messenger })
}

const confirmAdvance = async () => {
    const { api, headers } = useApi()
    const numbers = Object.fromEntries(
        Object.entries(preselectedNumbers.value)
            .filter(([_, arr]) => Array.isArray(arr) && arr.length > 0)
            .map(([key, arr]) => [key, arr.map(item => item.number)])
    )

    if (!Object.keys(numbers).length) return

    try {
        confirmLoading.value = true
        await api.post("/api/numbers/activate-in-advance", { numbers }, { headers })
        showToast(proxy.$moshaToast, "success", $t("page.avans1.success"))
        await router.push({ name: "Integration" })
    } catch (e) {
        const errorMessage = e.response?.data?.description || "Something went wrong"
        showToast(proxy.$moshaToast, "error", errorMessage, {
            timeout: 10000,
        })
    } finally {
        confirmLoading.value = false
    }
}

// Lifecycle hooks
onMounted(() => {
    window.addEventListener("resize", onResize)
    onResize()
    if (store.getters["auth/isLoggedIn"]) {
        loadNumbersData()
    }
})

onUnmounted(() => {
    unselectAllByMsg('viber')
    unselectAllByMsg('telegram')
})
</script>

<template>
    <div class="wrapper">
        <div class="top-page">
            <h2 class="header-title">{{ $t("page.tariffs.title.value") }}</h2>

            <div class="container-body">
                <div class="page">
                    <div class="information-block">
                        <HeaderMain class="avans-title">
                            {{ $t("page.avans1.mainheader") }}
                            <b>{{ this.$route.query.number }}</b>
                        </HeaderMain>
                        <div class="block">
                            <p>{{ $t("page.avans1.text") }}</p>
                        </div>
                    </div>
                    <div class="select-number-block">
                        <div class="select-number-block__head">
                            <HeaderMain class="avans-title">
                                {{ $t("page.integration.numbers_table.title") }}
                            </HeaderMain>
                            <ButtonLoading
                                class="btn avance-button-confirm"
                                :loading="confirmLoading"
                                :disabled="!getTotalSelectedNumbersCount"
                                @click="confirmAdvance"
                            >
                                {{
                                    `${$t("page.avans1.button")}${getTotalSelectedNumbersCount ? `: ${getTotalSelectedNumbersCount}` : ""}`
                                }}
                            </ButtonLoading>
                        </div>

                        <div class="selected-tables">
                            <div
                                v-for="msg in Object.values(messengers)"
                                :key="msg.value"
                                :class="`selected-numbers-${msg.value}`"
                            >
                                <HeaderTableBlock
                                    :title="$t('common.numbers')"
                                    :messenger="msg.value"
                                    :countTitle="paginationAllMessengers[msg.value][0]?.total"
                                    :loading="!!tableState[msg.value]?.isLoading"
                                    :sendInput="tableState[msg.value]?.search"
                                    @submitInput="getNumberDataBySearchValue($event, msg.value)"
                                />

                                <NumberTagsList
                                    v-if="preselectedNumbers[msg.value]?.length"
                                    :numbers="preselectedNumbers[msg.value]"
                                    :msg="msg.value"
                                    @unselect-all="unselectAllByMsg(msg.value)"
                                    @simple-unselect="(n) => unselectSimpleByMsg(msg.value, n)"
                                />

                                <!--          TAGS         -->
                                <div class="selected-number-table">
                                    <TableNumbersTariffs
                                        v-if="width > 1200"
                                        :columns="'page.tariffs.table'"
                                        :rowData="tableState[msg.value]?.data"
                                        :msg="msg"
                                        :check-if-selected="isSelectedNumber"
                                        @toggleSelectionHandler="toggleSelection"
                                        @loadMore="loadMore(msg.value)"
                                    >
                                        <template #information_column>
                                            <InfoDataTextRecurring
                                                v-show="tableState[msg.value]?.statusState"
                                                :value="tableState[msg.value]?.statusState"
                                                height="120px"
                                            />
                                        </template>
                                    </TableNumbersTariffs>

                                    <MobileTableRecurring
                                        v-else
                                        :Numbers="tableState[msg.value]?.data"
                                        :messenger="msg"
                                        table-type="selected"
                                        :check-if-selected="isSelectedNumber"
                                        @toggleSelectionHandler="toggleSelection"
                                    >
                                        <template #information_column>
                                            <InfoDataTextRecurring
                                                v-show="tableState[msg.value]?.statusState"
                                                :value="tableState[msg.value]?.statusState"
                                                height="120px"
                                            />
                                        </template>
                                        <template #pagination>
                                            <BasePagination
                                                :lastPage="paginationAllMessengers[msg.value][0]?.last_page"
                                                :currentPage="paginationAllMessengers[msg.value][0]?.current_page"
                                                @onChangePage="(numberPage) => changePage(numberPage, msg.value)"
                                            />
                                        </template>
                                    </MobileTableRecurring>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
@use "@/styles/variables" as *;

.wrapper {
    margin: 20px;
}

.header-title {
    text-transform: uppercase;
    font-size: 24px;
    font-weight: 500;
    margin-bottom: 26px;
}

.avance-button-confirm {
    text-transform: none;

    background: rgba($purple-color, 0.9);
    color: $white-color;
    height: 40px;
    min-width: 260px;
    max-width: 300px;
    width: 100%;
    border: 1px solid rgba($purple-color, 0.9);

    &:hover {
        border-color: $purple-color;
        background: $purple-color;
        color: $white-color;
    }
}

.avance-button-container {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 10px;
}

.custom-checkbox input[type="checkbox"] {
    display: none;
}

.custom-checkbox label {
    display: inline-block;
    width: 25px;
    height: 25px;
    border: 1px solid lightgrey;
    cursor: pointer;
    position: relative;
}

.custom-checkbox label::after {
    content: "";
    background-image: url("../assets/tariffs/check.png");
    background-size: contain;
    width: 12px;
    height: 12px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: none;
}

.custom-checkbox input[type="checkbox"]:checked + label::after {
    display: block;
}

.selected-numbers-input {
    width: 255px;
    height: 50px;
    border: 1px solid lightgrey;
    color: black;
    padding-left: 10px;
    border-radius: 6px;
}

.selected-tables {
    display: flex;
    flex-direction: column;
    gap: 40px;
}

.selected-numbers-viber {
    border-bottom: 1px solid lightgrey;
}

.select-number-block {
    .avans-title {
        margin-bottom: 0 !important;
    }

    margin-top: 30px;

    &__head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 30px;

        @media (max-width: $tablet-md-size) {
            flex-direction: column;
            align-items: flex-start;
        }
    }
}

.information-block {
    padding-bottom: 30px;
    border-bottom: 1px solid lightgray;
}

.avans-title {
    font-size: 24px;
}

.page {
    border: 1px solid lightgray;
    border-radius: 11px;
    padding: 20px;

    :deep(.txt) {
        font-weight: 500;
        margin-bottom: 30px;
    }

    .block {
        box-sizing: border-box;
        width: 100%;
        border-radius: 11px;
        background: var(--block-color);
        padding: 20px 30px;

        p {
            font-family: Gilroy;
            font-style: normal;
            font-weight: 500;
            font-size: 16px;
            line-height: 25px;
            color: var(--text-second-color);
        }

        .btn {
            margin: 30px 0 0 0;
            width: 230px;
        }
    }
}

@media screen and (max-width: 400px) {
    .page {
        max-width: 230px;
        margin-left: auto;
        margin-right: auto;
    }
}
</style>
